import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faList } from '@fortawesome/pro-light-svg-icons'

import {
  Button, DashboardModule, Form, SectionDivider,
} from '@domain-group/suit-theme'

import { deepSetObject } from '@domain-group/javascript-utils'

import useCurrentUser from '@hooks/useCurrentUser'
import useUser, { useUserForm } from '@hooks/useUser'

import PageContext from '@contexts/pageContext'

import ToggleListItem from '@components/ToggleListItem'

import timeZones from '@functions/timeZones'
import { toggleNestedValue } from '@functions/entity'
import { isAdmin } from '@functions/user'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const handleUpdateUser = (
  entityState: {},
  updateUser: (updateUserPayload: HandleCallbackActionParams) => void,
  updateFn: () => Promise<Object>,
) => {
  const updateUserPayload = {
    callbacks: {
      action: updateFn,
    },
    entityParams: entityState,
    toastText: 'User Updated',
  }

  // @ts-expect-error TS2345
  updateUser(updateUserPayload)
}

interface UserSettingsProps {
  userForm: ReturnType<typeof useUserForm>,
  userPayload: ReturnType<typeof useUser>,
}

const UserSettings = (props: UserSettingsProps) => {
  const { userForm, userPayload } = props

  const {
    callbacks: {
      manageUserApplications,
      updateUser: updateFn,
    },
    role,
  } = userPayload

  const pageContext = useContext(PageContext)
  const {
    callbacks: { updateUser },
  } = pageContext

  const {
    entityState,
    entityState: {
      api_user,
      data,
      data: {
        allowed_team_member_roles,
        template_skill_level,
      },
      options: {
        bookable,
        content_editor,
        enable_custom_realbase_applications,
      },
      time_zone,
    },
    errors,
  } = userForm

  const { isMasterAdmin } = useCurrentUser()

  return (
    <DashboardModule title="User Settings">
      <Form padding="large">
        <Form.Field errorMessage={errors.time_zone} label="Timezone">
          <select
            // @ts-expect-error TS2345
            onChange={e => handleUpdateUser({ time_zone: e.target.value }, updateUser, updateFn)}
            value={time_zone}
          >
            {timeZones.map(timezone => (
              <option key={timezone.key} value={timezone.value}>
                {timezone.title}
              </option>
            ))}
          </select>
        </Form.Field>

        {allowed_team_member_roles
          && allowed_team_member_roles.includes(
            'template_developer',
            'template_quality_controller',
            'template_supervisor',
          ) && (
            <Form.Field label="Template Skill Level" marginTop="large">
              <select
                data-value-type="number"
                onChange={e => handleUpdateUser(
                  { ...entityState, data: deepSetObject(data, 'template_skill_level', e.target.value) },
                  updateUser,
                  // @ts-expect-error TS2345
                  updateFn,
                )}
                value={template_skill_level}
              >
                <option value="1">Beginner</option>
                <option value="5">Intermediate</option>
                <option value="10">Advanced</option>
              </select>
            </Form.Field>
        )}

        <SectionDivider boxProps={{ marginY: 'large' }}>USER SETTINGS</SectionDivider>

        <ToggleListItem
          // @ts-expect-error TS2345
          onClick={() => handleUpdateUser(toggleNestedValue(userForm, 'options.bookable'), updateUser, updateFn)}
          toggleOnText="Yes"
          toggleOffText="No"
          toggleValue={bookable || false}
        >
          <ToggleListItem.Title>Bookable</ToggleListItem.Title>
          <ToggleListItem.Description>User can be booked for service products</ToggleListItem.Description>
        </ToggleListItem>

        <ToggleListItem
          // @ts-expect-error TS2345
          onClick={() => handleUpdateUser(toggleNestedValue(userForm, 'options.content_editor'), updateUser, updateFn)}
          toggleOnText="Yes"
          toggleOffText="No"
          toggleValue={content_editor || false}
        >
          <ToggleListItem.Title>Content Editor</ToggleListItem.Title>
          <ToggleListItem.Description>
            User can be assigned as an editor for contact services
          </ToggleListItem.Description>
        </ToggleListItem>

        <ToggleListItem
          boxProps={{ marginBottom: 'none' }}
          // @ts-expect-error TS2345
          onClick={() => handleUpdateUser({ api_user: !api_user }, updateUser, updateFn)}
          toggleOnText="Yes"
          toggleOffText="No"
          toggleValue={api_user || false}
        >
          <ToggleListItem.Title>API User</ToggleListItem.Title>
          <ToggleListItem.Description>User is managed via API</ToggleListItem.Description>
        </ToggleListItem>

        {/*
         // @ts-expect-error TS2339 */}
        {isMasterAdmin && isAdmin(role.title) && (
          <>
            <ToggleListItem
              boxProps={{ marginTop: 'large', marginBottom: 'none' }}
              onClick={() => handleUpdateUser(
                toggleNestedValue(userForm, 'options.enable_custom_realbase_applications'),
                updateUser,
                // @ts-expect-error TS2345
                updateFn,
              )}
              toggleOnText="Yes"
              toggleOffText="No"
              toggleValue={!enable_custom_realbase_applications || false}
            >
              <ToggleListItem.Title>Applications</ToggleListItem.Title>
              <ToggleListItem.Description>User can access all Applications</ToggleListItem.Description>
            </ToggleListItem>

            {enable_custom_realbase_applications && (
              <Button
                buttonStyle="secondaryUtility"
                // @ts-expect-error TS2322
                marginTop="large"
                icon={<FontAwesomeIcon icon={faList} />}
                onClick={() => manageUserApplications()}
                size="medium"
              >
                Manage Applications
              </Button>
            )}
          </>
        )}
      </Form>
    </DashboardModule>
  )
}

export default UserSettings
